import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Download,
  MapPin,
  RefreshCw,
  Save,
  Settings2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../components/layouts/AdminLayout";
import { academicCalendarService } from "../../services/academicCalendarService";
import { getActiveEnrollmentContext } from "../../services/enrollmentService";
import { summativeAssessmentTimetableService } from "../../services/summativeAssessmentTimetableService";

const emptySchedule = {
  scheduled_date: "",
  starts_at: "",
  ends_at: "",
  room_id: "",
  notes: "",
};

const periodName = (year, semester) => {
  const yearLabel =
    year?.name ||
    year?.code ||
    year?.academic_year ||
    (year?.start_date
      ? `${String(year.start_date).slice(0, 4)}-${String(year.end_date || "").slice(0, 4)}`
      : "Academic year");
  const semesterLabel =
    semester?.name ||
    semester?.code ||
    semester?.label ||
    semester?.term_name ||
    "Semester";
  return `${semesterLabel} · ${yearLabel}`;
};

const timeMinutes = (value) => {
  if (!value) return null;
  const [hours, minutes] = String(value).split(":").map(Number);
  return hours * 60 + (minutes || 0);
};

const toDateValue = (value) => (value ? String(value).slice(0, 10) : "");
const hoursFor = (unit, component) =>
  Number(
    component === "theory"
      ? unit?.exam_theory_hours
      : unit?.exam_practical_hours,
  ) || 0;

const isConfiguredTimetable = (value) =>
  Boolean(
    value?.assessment_starts_on &&
    value?.assessment_ends_on &&
    value?.assessment_starts_at &&
    value?.assessment_ends_at,
  );

const dateRange = (start, end) => {
  if (!start || !end || start > end) return [];
  const dates = [];
  const cursor = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  while (cursor <= last) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
};

const practicalStartDateFor = (settings) =>
  settings?.practical_assessment_starts_on || settings?.assessment_starts_on || "";

const formatTime = (minutes) =>
  String(Math.floor(minutes / 60)).padStart(2, "0") +
  ":" +
  String(minutes % 60).padStart(2, "0");

const hourlyTimelineFor = (settings) => {
  const dayStart = timeMinutes(settings?.assessment_starts_at);
  const dayEnd = timeMinutes(settings?.assessment_ends_at);
  if (dayStart === null || dayEnd === null || dayEnd <= dayStart) return [];
  const columns = [];
  for (let start = dayStart; start < dayEnd; start += 60) {
    const end = Math.min(start + 60, dayEnd);
    columns.push({
      key: `hour-${start}-${end}`,
      starts_at: formatTime(start),
      ends_at: formatTime(end),
      startMinutes: start,
      endMinutes: end,
    });
  }
  return columns;
};

const isHourlyBoundaryFor = (settings, value) => {
  const dayStart = timeMinutes(settings?.assessment_starts_at);
  const start = timeMinutes(value);
  return (
    dayStart !== null &&
    start !== null &&
    start >= dayStart &&
    (start - dayStart) % 60 === 0
  );
};

const overlaps = (leftStart, leftEnd, rightStart, rightEnd) =>
  timeMinutes(leftStart) < timeMinutes(rightEnd) &&
  timeMinutes(leftEnd) > timeMinutes(rightStart);

const theoryTransitionConflict = (
  leftStart,
  leftEnd,
  rightStart,
  rightEnd,
) => {
  const leftStartMinutes = timeMinutes(leftStart);
  const leftEndMinutes = timeMinutes(leftEnd);
  const rightStartMinutes = timeMinutes(rightStart);
  const rightEndMinutes = timeMinutes(rightEnd);
  if (
    [leftStartMinutes, leftEndMinutes, rightStartMinutes, rightEndMinutes].some(
      (value) => value === null,
    )
  )
    return false;
  if (
    leftStartMinutes < rightEndMinutes &&
    leftEndMinutes > rightStartMinutes
  )
    return true;
  const gap =
    leftStartMinutes >= rightEndMinutes
      ? leftStartMinutes - rightEndMinutes
      : rightStartMinutes >= leftEndMinutes
        ? rightStartMinutes - leftEndMinutes
        : 0;
  return gap < 60;
};

const moduleLabel = (unit) => unit?.module_in_level ?? unit?.module ?? "—";

const StructureLabel = ({ unit }) => (
  <span className="text-xs text-slate-500">
    Level {unit?.program_level ?? "—"} · Module {moduleLabel(unit)}
  </span>
);

const programmeLabel = (unit) => {
  const name =
    unit?.program?.name ||
    unit?.program_name ||
    unit?.programme_name ||
    "Programme not specified";
  const code = unit?.program?.code || unit?.program_code;
  return code && String(code) !== String(name) ? `${name} (${code})` : name;
};

const cohortLabel = (unit) =>
  [
    programmeLabel(unit),
    `Level ${unit?.program_level ?? "—"}`,
    `Module ${moduleLabel(unit)}`,
  ].join(" · ");

const classKeyFor = (unit) =>
  [
    unit?.program_id || unit?.program?.id || unit?.program_code || "programme",
    unit?.program_level ?? "—",
    moduleLabel(unit),
  ]
    .map((value) => String(value))
    .join("|");

const combinedGroupKeyFor = (unit) => {
  // A summative timetable is generated independently for each programme and
  // unit of competency. Teaching-group codes may still be retained on the
  // catalogue for teaching delivery, but they must not merge exam slots or
  // venues across units/programmes.
  return null;
};

const componentLabel = (unit, component) =>
  `${unit?.code || "Unit"} · ${cohortLabel(unit)} · ${component}`;

// Summative room capacity is based on candidates who have actually booked
// this period. Combined teaching groups receive the distinct group count from
// the capacity RPC, so the shared venue is checked once for the whole cohort.
const bookedCandidatesFor = (unit) =>
  Number(
    unit?.timetable_combined_booked_students ??
      unit?.timetable_booked_students ??
      0,
  ) || 0;

const roomLabel = (room) =>
  [room?.code, room?.name].filter(Boolean).join(" · ");

const departmentIdFor = (unit) =>
  unit?.department_id ||
  unit?.department?.id ||
  unit?.program?.department_id ||
  unit?.program?.department?.id ||
  null;

const roomTypeCategory = (value) => {
  const type = String(value || "").trim().toLowerCase();
  if (!type) return "";
  if (/(lecture|theory|classroom|seminar|exam|hall)/i.test(type))
    return "theory";
  if (
    /(lab|laboratory|workshop|practical|studio|field|kitchen|salon|maintenance|automotive|electrical|mechanical|welding|masonry|plumbing|electronics|drawing|fashion|agriculture|beauty|hospitality|food|beverage)/i.test(
      type,
    )
  )
    return "practical";
  return "";
};

const roomCompatibleFor = (unit, component, room, teachingRoomIds = null) => {
  // Assessment rooms are not a free-standing pool. They must be one of the
  // rooms assigned to this unit in the teaching timetable for this exact
  // academic year and semester. An empty list deliberately means no room is
  // eligible, so the generator cannot silently fall back to another venue.
  if (Array.isArray(teachingRoomIds)) {
    if (!teachingRoomIds.length || !teachingRoomIds.some((id) => String(id) === String(room?.id)))
      return false;
  }
  const unitDepartmentId = departmentIdFor(unit);
  if (
    unitDepartmentId &&
    room?.department_id &&
    String(room.department_id) !== String(unitDepartmentId)
  )
    return false;

  const roomCategory = roomTypeCategory(room?.room_type);
  const requiredType = unit?.required_room_type || unit?.room_type;
  const requiredCategory = roomTypeCategory(requiredType);
  const effectiveRequiredCategory =
    component === "practical" && requiredCategory === "theory"
      ? ""
      : requiredCategory;

  // Practical assessments must use practical spaces. A legacy
  // "Standard Classroom" default on a unit must not override that rule.
  if (component === "practical" && roomCategory === "theory") return false;
  if (requiredType && effectiveRequiredCategory && roomCategory)
    return effectiveRequiredCategory === roomCategory;
  if (component === "theory" && roomCategory === "practical") return false;
  return true;
};

const TimetableCell = ({
  unit,
  component,
  value,
  onChange,
  onSave,
  saving,
  disabled,
  settings,
  rooms = [],
  teachingRoomIds = [],
}) => {
  const hours = hoursFor(unit, component);
  const compatibleRooms = rooms.filter((room) =>
    roomCompatibleFor(unit, component, room, teachingRoomIds) &&
    Number(room?.capacity || 0) >= bookedCandidatesFor(unit),
  );
  if (!hours)
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-400">
        Not required — no {component} hours declared
      </div>
    );
  const hasDate = Boolean(value?.scheduled_date);
  return (
    <div
      className={`space-y-2 rounded-lg border p-3 ${disabled ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-slate-800">
          {hours} hour{hours === 1 ? "" : "s"}
        </span>
        <span className="text-[0.65rem] font-medium text-slate-500">
          {bookedCandidatesFor(unit)} booked
        </span>
        {hasDate && (
          <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Scheduled
          </span>
        )}
      </div>
      <input
        type="date"
        aria-label={`${unit.code} ${component} assessment date`}
        value={value?.scheduled_date || ""}
        onChange={(e) => onChange("scheduled_date", e.target.value)}
        disabled={disabled}
        min={
          component === "practical"
            ? practicalStartDateFor(settings)
            : settings?.assessment_starts_on || undefined
        }
        max={settings?.assessment_ends_on || undefined}
        className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          type="time"
          aria-label={`${unit.code} ${component} start time`}
          value={value?.starts_at || ""}
          onChange={(e) => onChange("starts_at", e.target.value)}
          disabled={disabled}
          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs"
        />
        <input
          type="time"
          aria-label={`${unit.code} ${component} end time`}
          value={value?.ends_at || ""}
          onChange={(e) => onChange("ends_at", e.target.value)}
          disabled={disabled}
          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs"
        />
      </div>
      <label className="block space-y-1">
        <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold text-slate-600">
          <MapPin className="h-3 w-3" /> Teaching timetable room
        </span>
        <select
          aria-label={`${unit.code} ${component} assessment venue`}
          value={value?.room_id || ""}
          onChange={(e) => onChange("room_id", e.target.value)}
          disabled={disabled}
          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs"
        >
          <option value="">Venue to be assigned</option>
          {compatibleRooms.map((room) => (
            <option key={room.id} value={room.id}>
              {roomLabel(room)}
              {room.room_type ? ` · ${room.room_type}` : ""}
              {room.capacity ? ` · ${room.capacity} seats` : ""}
            </option>
          ))}
        </select>
        {!compatibleRooms.length && (
          <span className="block text-[0.62rem] font-medium text-amber-700">
            {bookedCandidatesFor(unit) > 0 &&
            !rooms.some(
              (room) =>
                roomCompatibleFor(unit, component, room, teachingRoomIds) &&
                Number(room?.capacity || 0) >= bookedCandidatesFor(unit),
            ) &&
            rooms.some((room) =>
              roomCompatibleFor(unit, component, room, teachingRoomIds),
            )
              ? `No compatible venue has capacity for ${bookedCandidatesFor(unit)} booked candidate${bookedCandidatesFor(unit) === 1 ? "" : "s"}.`
              : !teachingRoomIds.length
              ? "No teaching timetable room is declared for this unit in this academic period."
              : "No active teaching timetable room matches this assessment component."}
          </span>
        )}
      </label>
      <button
        type="button"
        onClick={onSave}
        disabled={disabled || saving || !settings}
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Save className="h-3.5 w-3.5" />
        {saving ? "Saving…" : hasDate ? "Update slot" : "Schedule"}
      </button>
    </div>
  );
};

const timetableDateLabel = (value) => {
  const [year, month, day] = String(value || "")
    .split("-")
    .map(Number);
  return year && month && day
    ? `${day}/${String(month).padStart(2, "0")}/${year}`
    : value || "—";
};

const SUMMATIVE_REPORT_OPTIONS = [
  { value: "programme", label: "Programme / level / module" },
  { value: "unit", label: "Programme / unit of competency" },
  { value: "trainer", label: "Chief invigilator" },
  { value: "room", label: "Room / venue" },
];

const summativeReportMeta = (entry, reportView) => {
  const unit = entry?.unit || {};
  const programme = programmeLabel(unit);
  const level = unit.program_level ?? "—";
  const module = moduleLabel(unit);
  const trainer = unit.chief_invigilator?.full_name || unit.chief_invigilator?.name || "Invigilator not assigned";
  const room = roomLabel(entry?.room);
  if (reportView === "unit") {
    return {
      key: `unit:${unit.program_id || unit.program?.id || programme}:${unit.id || entry?.unit_id || entry?.id}`,
      label: `${programme} · ${unit.code || "Unit"}`,
      subLabel: `${unit.name || "Unit of competency"} · Level ${level} · Module ${module}`,
    };
  }
  if (reportView === "trainer") {
    return {
      key: `trainer:${unit.chief_invigilator?.id || trainer}`,
      label: trainer,
      subLabel: "Chief invigilator",
    };
  }
  if (reportView === "room") {
    return {
      key: `room:${entry?.room_id || entry?.room?.id || room}`,
      label: room,
      subLabel: entry?.room?.room_type || "Assessment venue",
    };
  }
  return {
    key: `programme:${unit.program_id || unit.program?.id || programme}|${level}|${module}`,
    label: programme,
    subLabel: `Level ${level} · Module ${module}`,
  };
};

const GeneratedTimetableGrid = ({ settings, entries, units, periodLabel, reportView = "unit" }) => {
  const unitById = Object.fromEntries(
    units.map((unit) => [String(unit.id), unit]),
  );
  const timelineColumns = hourlyTimelineFor(settings);
  const dates = dateRange(
    settings?.assessment_starts_on,
    settings?.assessment_ends_on,
  );
  const scheduledEntries = entries
    .filter((entry) => entry.status !== "cancelled" && entry.scheduled_date)
    .map((entry) => ({
      ...entry,
      unit: {
        ...(unitById[String(entry.unit_id)] || {}),
        ...(entry.unit || {}),
      },
    }));
  const groups = [];
  const groupMap = new Map();
  scheduledEntries.forEach((entry) => {
    const meta = summativeReportMeta(entry, reportView);
    const key = meta.key;
    if (!groupMap.has(key)) {
      const group = {
        key,
        label: meta.label,
        subLabel: meta.subLabel,
        entries: [],
      };
      groupMap.set(key, group);
      groups.push(group);
    }
    const group = groupMap.get(key);
    group.entries.push(entry);
  });
  if (!scheduledEntries.length || !dates.length || !timelineColumns.length)
    return null;

  const cellsForDate = (group, date) => {
    const dateEntries = group.entries.filter(
      (entry) => toDateValue(entry.scheduled_date) === date,
    );
    const placed = new Set();
    const cells = [];
    let index = 0;
    while (index < timelineColumns.length) {
      const column = timelineColumns[index];
      const first = dateEntries
        .filter((entry) => !placed.has(entry.id))
        .map((entry) => ({
          entry,
          start: timeMinutes(entry.starts_at),
          end: timeMinutes(entry.ends_at),
        }))
        .filter(
          ({ start }) =>
            start !== null &&
            start >= column.startMinutes &&
            start < column.endMinutes,
        )
        .sort((left, right) => left.start - right.start)[0];

      if (first) {
        const blockEntries = [first.entry];
        let blockEnd = first.end;
        let changed = true;
        while (changed) {
          changed = false;
          dateEntries.forEach((entry) => {
            if (placed.has(entry.id) || blockEntries.includes(entry)) return;
            const start = timeMinutes(entry.starts_at);
            const end = timeMinutes(entry.ends_at);
            if (
              start !== null &&
              end !== null &&
              start < blockEnd &&
              end > column.startMinutes
            ) {
              blockEntries.push(entry);
              blockEnd = Math.max(blockEnd, end);
              changed = true;
            }
          });
        }
        blockEntries.forEach((entry) => placed.add(entry.id));
        let lastIndex = index;
        while (
          lastIndex + 1 < timelineColumns.length &&
          timelineColumns[lastIndex].endMinutes < blockEnd
        ) {
          lastIndex += 1;
        }
        cells.push({
          key: `${date}-${column.key}`,
          entries: blockEntries,
          colSpan: lastIndex - index + 1,
        });
        index = lastIndex + 1;
        continue;
      }

      cells.push({
        key: `${date}-${column.key}`,
        entries: [],
        colSpan: 1,
      });
      index += 1;
    }
    return cells;
  };

  const renderEntries = (cellEntries) =>
    cellEntries.length ? (
      <div className="space-y-2">
        {cellEntries.map((entry) => (
          <div key={entry.id} className="font-semibold text-slate-900">
            <div className="font-mono text-[0.68rem] text-primary">
              {entry.unit?.code || "Unit"}
              <span className="ml-1 text-[0.58rem] uppercase text-slate-500">
                {entry.assessment_component === "practical" ? "P" : "T"}
              </span>
            </div>
            <div className="mt-0.5 text-[0.58rem] font-medium text-slate-500">
              {roomLabel(entry.room) || "Venue TBD"}
            </div>
            {(reportView === "trainer" || reportView === "room") && (
              <div className="mt-0.5 text-[0.56rem] font-medium text-slate-500">
                {programmeLabel(entry.unit)} · L{entry.unit?.program_level ?? "—"} M{moduleLabel(entry.unit)}
              </div>
            )}
          </div>
        ))}
      </div>
    ) : (
      <span className="text-slate-300">—</span>
    );

  return (
    <section
      id="summative-assessment-timetable"
      className="overflow-hidden rounded-2xl border-2 border-slate-700 bg-white shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-300 bg-slate-50 px-5 py-4">
        <div>
          <h2 className="text-base font-bold uppercase tracking-wide text-slate-950">
            Generated summative examination timetable
          </h2>
          <div className="mt-1 text-xs font-medium text-slate-600">
            {periodLabel}
          </div>
        </div>
        <span className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700">
          {scheduledEntries.length} scheduled component
          {scheduledEntries.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-max border-collapse text-left text-[0.68rem]">
          <thead>
            <tr className="bg-slate-100">
              <th
                rowSpan={2}
                className="sticky left-0 z-10 min-w-[210px] border-2 border-slate-700 bg-slate-100 px-3 py-3 font-bold uppercase tracking-wide text-slate-800"
              >
                {SUMMATIVE_REPORT_OPTIONS.find((option) => option.value === reportView)?.label}
              </th>
              {dates.map((date) => (
                <th
                  key={date}
                  colSpan={timelineColumns.length}
                  className="border-2 border-slate-700 px-3 py-2 text-center text-sm font-bold text-slate-900"
                >
                  {timetableDateLabel(date)}
                </th>
              ))}
            </tr>
            <tr className="bg-slate-100">
              {dates.flatMap((date) =>
                timelineColumns.map((column) => (
                  <th
                    key={date + "-" + column.key}
                    className="min-w-[110px] border-2 border-slate-700 px-2 py-2 text-center font-semibold text-slate-700"
                  >
                    <span className="block">{column.starts_at}</span>
                    <span className="block">{column.ends_at}</span>
                  </th>
                )),
              )}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.key} className="align-top">
                <th className="sticky left-0 z-10 min-w-[210px] border-2 border-slate-700 bg-white px-3 py-3 font-bold uppercase text-slate-800">
                  <span className="block">{group.label}</span>
                  <span className="mt-1 block text-[0.62rem] font-semibold normal-case text-primary">
                    {group.subLabel}
                  </span>
                </th>
                {dates.flatMap((date) =>
                  cellsForDate(group, date).map((cell) => (
                    <td
                      key={cell.key}
                      colSpan={cell.colSpan}
                      className="min-w-[110px] border-2 border-slate-700 px-2 py-2 text-center align-top"
                    >
                      {renderEntries(cell.entries)}
                    </td>
                  )),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const SummativeAssessmentScheduleManagement = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState({
    academicYearId: "",
    semesterId: "",
  });
  const [settings, setSettings] = useState(null);
  const [units, setUnits] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [teachingRoomsByUnit, setTeachingRoomsByUnit] = useState({});
  const [teachingTimetableIssue, setTeachingTimetableIssue] = useState("");
  const [entries, setEntries] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [message, setMessage] = useState(null);
  const [settingsIssue, setSettingsIssue] = useState("");
  const [reportView, setReportView] = useState("unit");
  const [selectedProgramId, setSelectedProgramId] = useState("all");
  const [selectedUnitId, setSelectedUnitId] = useState("all");

  const timetableReady = isConfiguredTimetable(settings);

  const teachingRoomIdsFor = (unit) =>
    teachingRoomsByUnit[String(unit?.id)] || [];

  const selected = useMemo(
    () =>
      periods.find(
        (period) =>
          period.year.id === selectedPeriod.academicYearId &&
          period.semester.id === selectedPeriod.semesterId,
      ),
    [periods, selectedPeriod],
  );

  const programmeOptions = useMemo(() => {
    const values = new Map();
    units.forEach((unit) => {
      const program = unit?.program || {};
      const id = unit?.program_id || program.id || unit?.program_code;
      const name = program.name || unit?.program_name || unit?.programme_name || unit?.program_code;
      if (!id || !name) return;
      values.set(String(id), {
        id: String(id),
        name: String(name),
        code: program.code || unit?.program_code || "",
      });
    });
    return [...values.values()].sort((left, right) => left.name.localeCompare(right.name));
  }, [units]);

  const visibleUnits = useMemo(() => units.filter((unit) => {
    const unitProgramId = unit?.program_id || unit?.program?.id || unit?.program_code;
    if (selectedProgramId !== "all" && String(unitProgramId || "") !== String(selectedProgramId)) return false;
    if (selectedUnitId !== "all" && String(unit?.id || "") !== String(selectedUnitId)) return false;
    return true;
  }), [units, selectedProgramId, selectedUnitId]);

  const unitOptions = useMemo(() => visibleUnits
    .map((unit) => ({ id: String(unit.id), code: unit.code, name: unit.name }))
    .sort((left, right) => `${left.code} ${left.name}`.localeCompare(`${right.code} ${right.name}`)), [visibleUnits]);

  const visibleEntryIds = useMemo(() => new Set(visibleUnits.map((unit) => String(unit.id))), [visibleUnits]);
  const visibleEntries = useMemo(() => entries.filter((entry) => visibleEntryIds.has(String(entry.unit_id))), [entries, visibleEntryIds]);
  const scheduleScopeLabel = useMemo(() => {
    const programme = programmeOptions.find((option) => option.id === String(selectedProgramId));
    const unit = units.find((option) => String(option.id) === String(selectedUnitId));
    if (unit) return `${programmeLabel(unit)} · ${unit.code || unit.name}`;
    if (programme) return programme.name;
    return "all programmes and units";
  }, [programmeOptions, selectedProgramId, selectedUnitId, units]);

  useEffect(() => {
    if (selectedProgramId !== "all" && !programmeOptions.some((option) => option.id === String(selectedProgramId))) {
      setSelectedProgramId("all");
    }
  }, [programmeOptions, selectedProgramId]);

  useEffect(() => {
    if (selectedUnitId !== "all" && !unitOptions.some((option) => option.id === String(selectedUnitId))) {
      setSelectedUnitId("all");
    }
  }, [selectedUnitId, unitOptions]);

  const loadPeriods = async () => {
    setLoading(true);
    try {
      const [years, activeResult] = await Promise.all([
        academicCalendarService.getAcademicYears(),
        getActiveEnrollmentContext(),
      ]);
      const groups = await Promise.all(
        (years || []).map(async (year) => ({
          year,
          semesters: await academicCalendarService.getSemesters(year.id),
        })),
      );
      const allPeriods = groups.flatMap((group) =>
        (group.semesters || []).map((semester) => ({
          year: group.year,
          semester,
        })),
      );
      setPeriods(allPeriods);
      const active = activeResult?.success ? activeResult.data : null;
      const preferred =
        allPeriods.find(
          (period) =>
            period.year.id === active?.academicYear?.id &&
            period.semester.id === active?.semester?.id,
        ) ||
        allPeriods.find(
          (period) =>
            String(period.year.status).toLowerCase() === "active" &&
            String(period.semester.status).toLowerCase() === "active",
        );
      if (preferred)
        setSelectedPeriod({
          academicYearId: preferred.year.id,
          semesterId: preferred.semester.id,
        });
      else
        setMessage({
          type: "warning",
          text: "No active academic year and semester are available. Activate the current period in Calendar before scheduling assessments.",
        });
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "Academic periods could not be loaded.",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPeriodData = async () => {
    if (!selectedPeriod.academicYearId || !selectedPeriod.semesterId) {
      setSettings(null);
      setRooms([]);
      setTeachingRoomsByUnit({});
      setTeachingTimetableIssue("");
      setEntries([]);
      setDrafts({});
      return;
    }
    setPeriodLoading(true);
    setMessage(null);
    setSettingsIssue("");
    try {
      const [
        settingsResult,
        entriesResult,
        unitsResult,
        roomsResult,
        teachingRoomsResult,
      ] =
        await Promise.allSettled([
          summativeAssessmentTimetableService.getSettings(
            selectedPeriod.academicYearId,
            selectedPeriod.semesterId,
          ),
          summativeAssessmentTimetableService.getEntries(
            selectedPeriod.academicYearId,
            selectedPeriod.semesterId,
          ),
          summativeAssessmentTimetableService.getEligibleUnits(
            selectedPeriod.academicYearId,
            selectedPeriod.semesterId,
          ),
          summativeAssessmentTimetableService.getRooms(),
          summativeAssessmentTimetableService.getTeachingRoomAssignments(
            selectedPeriod.academicYearId,
            selectedPeriod.semesterId,
          ),
        ]);
      const periodSettings =
        settingsResult.status === "fulfilled" ? settingsResult.value : null;
      const periodEntries =
        entriesResult.status === "fulfilled" ? entriesResult.value : [];
      const eligibleUnits =
        unitsResult.status === "fulfilled" ? unitsResult.value : [];
      const activeRooms =
        roomsResult.status === "fulfilled" ? roomsResult.value : [];
      const teachingRoomRows =
        teachingRoomsResult.status === "fulfilled"
          ? teachingRoomsResult.value || []
          : [];
      const nextTeachingRoomsByUnit = {};
      teachingRoomRows.forEach((row) => {
        const unitId = row?.unit_id || row?.unit?.id || row?.session?.unit_id;
        const roomId = row?.room_id || row?.room?.id || row?.session?.room_id;
        if (!unitId || !roomId) return;
        const key = String(unitId);
        if (!nextTeachingRoomsByUnit[key]) nextTeachingRoomsByUnit[key] = [];
        if (!nextTeachingRoomsByUnit[key].some((id) => String(id) === String(roomId)))
          nextTeachingRoomsByUnit[key].push(roomId);
      });
      setSettings(periodSettings);
      setRooms(activeRooms);
      setTeachingRoomsByUnit(nextTeachingRoomsByUnit);
      setTeachingTimetableIssue(
        teachingRoomsResult.status === "rejected"
          ? teachingRoomsResult.reason?.message ||
              "Teaching timetable rooms could not be loaded for this academic period."
          : "",
      );
      setUnits(eligibleUnits);
      const isCurrentActiveSemester =
        String(selected?.year?.status || "").toLowerCase() === "active" &&
        String(selected?.semester?.status || "").toLowerCase() === "active";
      // A stale timetable row must not make an inactive programme appear in
      // the current timetable. Historical periods retain their saved rows for
      // audit/review, while the active semester is strictly limited to the
      // units returned by the period-enrolment RPC.
      const visibleEntries =
        unitsResult.status === "fulfilled" && isCurrentActiveSemester
          ? periodEntries.filter((entry) =>
              eligibleUnits.some(
                (unit) => String(unit.id) === String(entry.unit_id),
              ),
            )
          : periodEntries;
      setEntries(visibleEntries);
      const nextDrafts = {};
      (visibleEntries || []).forEach((entry) => {
        nextDrafts[`${entry.unit_id}:${entry.assessment_component}`] = {
          scheduled_date: toDateValue(entry.scheduled_date),
          starts_at: String(entry.starts_at || "").slice(0, 5),
          ends_at: String(entry.ends_at || "").slice(0, 5),
          room_id: entry.room_id || entry.room?.id || "",
          notes: entry.notes || "",
        };
      });
      setDrafts(nextDrafts);
      const periodLabel = selected
        ? periodName(selected.year, selected.semester)
        : "the selected academic period";
      if (settingsResult.status === "rejected") {
        setSettingsIssue(
          settingsResult.reason?.message ||
            "Assessment timetable settings could not be loaded.",
        );
      } else if (!periodSettings) {
        setSettingsIssue(
          `No assessment timetable settings have been saved for ${periodLabel}. Configure this period in System Settings → Timetable, then save.`,
        );
      } else if (!isConfiguredTimetable(periodSettings)) {
        setSettingsIssue(
          `The assessment timetable settings for ${periodLabel} are incomplete. Save the exam dates, practical start date, day hours and break in System Settings → Timetable.`,
        );
      }
      const failedResult = [
        settingsResult,
        entriesResult,
        unitsResult,
        roomsResult,
        teachingRoomsResult,
      ].find(
        (result) => result.status === "rejected",
      );
      if (failedResult) {
        setMessage({
          type: "error",
          text:
            failedResult.reason?.message ||
            "Some timetable data could not be loaded.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error?.message || "The selected academic period could not be loaded.",
      });
    } finally {
      setPeriodLoading(false);
    }
  };

  useEffect(() => {
    loadPeriods();
  }, []);
  useEffect(() => {
    loadPeriodData();
  }, [selectedPeriod.academicYearId, selectedPeriod.semesterId]);

  const draftFor = (unit, component) => {
    const existing = drafts[`${unit.id}:${component}`];
    if (existing) return existing;
    const teachingRoomIds = teachingRoomIdsFor(unit);
    const startsAt = String(settings?.assessment_starts_at || "").slice(0, 5);
    const endMinutes =
      timeMinutes(startsAt) + Math.round(hoursFor(unit, component) * 60);
    return {
      ...emptySchedule,
      room_id: teachingRoomIds.length === 1 ? teachingRoomIds[0] : "",
      starts_at: startsAt,
      ends_at:
        startsAt && Number.isFinite(endMinutes) ? formatTime(endMinutes) : "",
    };
  };
  const updateDraft = (unit, component, field, value) => {
    const key = `${unit.id}:${component}`;
    setDrafts((prev) => {
      const next = { ...draftFor(unit, component), [field]: value };
      return { ...prev, [key]: next };
    });
  };

  const validateDraft = (unit, component, draft) => {
    if (!timetableReady)
      return "Save the assessment window, day hours and dates in System Settings before scheduling units.";
    if (!draft.scheduled_date || !draft.starts_at || !draft.ends_at)
      return "Date, start time and end time are required.";
    if (!draft.room_id)
      return "Select an assessment venue/room before saving this timetable slot.";
    const selectedRoom = rooms.find(
      (room) => String(room.id) === String(draft.room_id),
    );
    if (!selectedRoom || String(selectedRoom.status || "active").toLowerCase() !== "active")
      return "Select an active assessment venue/room.";
    const teachingRoomIds = teachingRoomIdsFor(unit);
    if (!teachingRoomIds.length)
      return "This unit has no room declared in the teaching timetable for the selected academic period.";
    if (!teachingRoomIds.some((id) => String(id) === String(selectedRoom.id)))
      return "The assessment room must be the room declared for this unit in the teaching timetable.";
    if (!roomCompatibleFor(unit, component, selectedRoom, teachingRoomIds))
      return component === "theory"
        ? "Theory assessment must use the unit's lecturer/teaching timetable room."
        : "Practical assessment must use the unit's declared practical teaching room.";
    if (draft.starts_at >= draft.ends_at)
      return "End time must be later than start time.";
    const durationHours =
      (timeMinutes(draft.ends_at) - timeMinutes(draft.starts_at)) / 60;
    if (
      Math.round(durationHours * 100) / 100 !==
      Math.round(hoursFor(unit, component) * 100) / 100
    )
      return `The slot must be exactly ${hoursFor(unit, component)} hour(s).`;
    const componentStartDate =
      component === "practical"
        ? practicalStartDateFor(settings)
        : settings.assessment_starts_on;
    if (componentStartDate && draft.scheduled_date < componentStartDate)
      return component === "practical"
        ? "Date is before the configured practical assessment start date."
        : "Date is before the configured assessment period.";
    if (
      settings.assessment_ends_on &&
      draft.scheduled_date > settings.assessment_ends_on
    )
      return "Date is after the configured assessment period.";
    if (
      settings.assessment_starts_at &&
      draft.starts_at < String(settings.assessment_starts_at).slice(0, 5)
    )
      return "Start time is before the assessment day starts.";
    if (!isHourlyBoundaryFor(settings, draft.starts_at))
      return "Start time must align with the one-hour assessment grid.";
    if (
      settings.assessment_ends_at &&
      draft.ends_at > String(settings.assessment_ends_at).slice(0, 5)
    )
      return "End time is after the assessment day ends.";
    if (
      settings.break_starts_at &&
      timeMinutes(draft.starts_at) < timeMinutes(settings.break_ends_at) &&
      timeMinutes(draft.ends_at) > timeMinutes(settings.break_starts_at)
    )
      return "The slot overlaps the configured assessment break.";
    if (!hoursFor(unit, component))
      return `No ${component} assessment hours are declared for this unit.`;

    // Keep manual edits subject to the same cohort rules as automatic
    // generation. A cohort is programme + level + module; cancelled rows and
    // the slot currently being edited do not participate in the checks.
    const sameCohortEntries = entries.filter((entry) => {
      if (
        entry.status === "cancelled" ||
        String(entry.unit_id) === String(unit.id) ||
        toDateValue(entry.scheduled_date) !== draft.scheduled_date
      )
        return false;
      const existingUnit =
        entry.unit || units.find((candidate) => String(candidate.id) === String(entry.unit_id));
      return existingUnit && classKeyFor(existingUnit) === classKeyFor(unit);
    });
    for (const entry of sameCohortEntries) {
      const existingUnit =
        entry.unit || units.find((candidate) => String(candidate.id) === String(entry.unit_id));
      const sameCombinedGroup =
        combinedGroupKeyFor(unit) &&
        combinedGroupKeyFor(existingUnit) === combinedGroupKeyFor(unit) &&
        entry.assessment_component === component;
      if (sameCombinedGroup) continue;
      if (component === "practical" && entry.assessment_component === "practical")
        return "A cohort may have only one practical assessment per day.";
      if (component === "practical" && entry.assessment_component === "theory")
        return "A cohort cannot have a practical assessment on the same day as a theory paper.";
      if (component === "theory" && entry.assessment_component === "practical")
        return "A cohort cannot have a theory paper on the same day as a practical assessment.";
      if (
        component === "theory" &&
        entry.assessment_component === "theory" &&
        theoryTransitionConflict(
          draft.starts_at,
          draft.ends_at,
          String(entry.starts_at || "").slice(0, 5),
          String(entry.ends_at || "").slice(0, 5),
        )
      )
        return "Theory papers for the same cohort require at least one hour between exams.";
    }
    return null;
  };

  const saveDraft = async (unit, component) => {
    const key = `${unit.id}:${component}`;
    const draft = draftFor(unit, component);
    const validationError = validateDraft(unit, component, draft);
    if (validationError) {
      setMessage({ type: "error", text: `${unit.code}: ${validationError}` });
      return;
    }
    setSavingKey(key);
    setMessage(null);
    try {
      const saved = await summativeAssessmentTimetableService.saveEntry({
        academicYearId: selectedPeriod.academicYearId,
        semesterId: selectedPeriod.semesterId,
        unitId: unit.id,
        assessmentComponent: component,
        scheduledDate: draft.scheduled_date,
        startsAt: draft.starts_at,
        endsAt: draft.ends_at,
        sessionId: null,
        roomId: draft.room_id || null,
        notes: draft.notes,
      });
      setEntries((prev) => [
        ...prev.filter(
          (entry) =>
            !(
              entry.unit_id === unit.id &&
              entry.assessment_component === component
            ),
        ),
        saved,
      ]);
      setDrafts((prev) => ({ ...prev, [key]: { ...draft } }));
      setMessage({
        type: "success",
        text: `${unit.code} ${component} assessment slot saved for ${periodName(selected?.year, selected?.semester)}.`,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "Assessment slot could not be saved.",
      });
    } finally {
      setSavingKey("");
    }
  };

  const generateDraftTimetable = async () => {
    if (!timetableReady) {
      setMessage({
        type: "error",
        text: "Save the period-specific assessment window, dates and day hours first.",
      });
      return;
    }
    const dates = dateRange(
      settings.assessment_starts_on,
      settings.assessment_ends_on,
    );
    const hourlyColumns = hourlyTimelineFor(settings);
    if (!hourlyColumns.length || !dates.length) {
      setMessage({
        type: "error",
        text: "Set valid assessment dates and assessment-day start/end times before generating.",
      });
      return;
    }

    const targetUnits = visibleUnits;
    if (!targetUnits.length) {
      setMessage({
        type: "error",
        text: `No summative assessment units are available for ${scheduleScopeLabel}.`,
      });
      return;
    }
    const targetUnitIds = new Set(targetUnits.map((unit) => String(unit.id)));

    const unitById = Object.fromEntries(
      units.map((unit) => [String(unit.id), unit]),
    );
    const practicalAnchorTimes = new Set(
      hourlyColumns.map((column) => column.starts_at),
    );
    // Older generated drafts may have been placed at arbitrary minute
    // offsets. Allow only those non-hourly drafts to be regenerated; a start
    // such as 10:30 is valid when the day starts at 08:30.
    const entriesToReschedule = new Set(
      entries
        .filter((entry) => {
          if (!targetUnitIds.has(String(entry.unit_id))) return false;
          if (String(entry.status || "draft").toLowerCase() !== "draft")
            return false;
          const entryUnit = entry.unit || unitById[String(entry.unit_id)];
          const assignedTeachingRooms = entryUnit
            ? teachingRoomIdsFor(entryUnit)
            : [];
          const roomId = entry.room_id || entry.room?.id || "";
          const roomMismatch =
            assignedTeachingRooms.length > 0 &&
            !assignedTeachingRooms.some(
              (id) => String(id) === String(roomId),
            );
          return (
            !practicalAnchorTimes.has(
              String(entry.starts_at || "").slice(0, 5),
            ) || roomMismatch
          );
        })
        .map((entry) => entry.unit_id + ":" + entry.assessment_component),
    );

    // Drafts created before the cohort transition rules may also contain
    // back-to-back theory papers or a practical on a theory day. Release both
    // sides of every such draft conflict so the generator can reshuffle them.
    const draftEntries = entries.filter(
      (entry) =>
        targetUnitIds.has(String(entry.unit_id)) &&
        String(entry.status || "draft").toLowerCase() === "draft" &&
        entry.status !== "cancelled" &&
        entry.scheduled_date,
    );
    draftEntries.forEach((left, leftIndex) => {
      const leftUnit = left.unit || unitById[String(left.unit_id)];
      if (!leftUnit) return;
      draftEntries.slice(leftIndex + 1).forEach((right) => {
        const rightUnit = right.unit || unitById[String(right.unit_id)];
        if (
          !rightUnit ||
          toDateValue(left.scheduled_date) !== toDateValue(right.scheduled_date) ||
          classKeyFor(leftUnit) !== classKeyFor(rightUnit)
        )
          return;
        const sameCombinedGroup =
          combinedGroupKeyFor(leftUnit) &&
          combinedGroupKeyFor(leftUnit) === combinedGroupKeyFor(rightUnit) &&
          left.assessment_component === right.assessment_component;
        if (sameCombinedGroup) return;
        const practicalConflict =
          left.assessment_component === "practical" ||
          right.assessment_component === "practical";
        const theoryConflict =
          left.assessment_component === "theory" &&
          right.assessment_component === "theory" &&
          theoryTransitionConflict(
            String(left.starts_at || "").slice(0, 5),
            String(left.ends_at || "").slice(0, 5),
            String(right.starts_at || "").slice(0, 5),
            String(right.ends_at || "").slice(0, 5),
          );
        if (practicalConflict || theoryConflict) {
          entriesToReschedule.add(left.unit_id + ":" + left.assessment_component);
          entriesToReschedule.add(right.unit_id + ":" + right.assessment_component);
        }
      });
    });
    const existingKeys = new Set(
      entries
        .filter(
          (entry) =>
            !entriesToReschedule.has(
              entry.unit_id + ":" + entry.assessment_component,
            ),
        )
        .map((entry) => entry.unit_id + ":" + entry.assessment_component),
    );
    const activeRooms = (rooms || []).filter(
      (room) =>
        room?.id && String(room.status || "active").toLowerCase() === "active",
    );
    if (!activeRooms.length) {
      setMessage({
        type: "error",
        text: "No active rooms are configured. Add an active venue in Rooms before generating the assessment timetable.",
      });
      return;
    }
    const reservations = entries
      .filter(
        (entry) =>
          entry.status !== "cancelled" &&
          !entriesToReschedule.has(
            entry.unit_id + ":" + entry.assessment_component,
          ),
      )
      .map((entry) => ({
        reservationKey: null,
        component: entry.assessment_component,
        trainerId:
          entry.chief_invigilator_id ||
          unitById[String(entry.unit_id)]?.chief_invigilator?.id ||
          null,
        classKey: classKeyFor(
          entry.unit || unitById[String(entry.unit_id)] || {},
        ),
        combinedGroupKey: combinedGroupKeyFor(
          entry.unit || unitById[String(entry.unit_id)] || {},
        ),
        roomId: entry.room_id || entry.room?.id || null,
        date: toDateValue(entry.scheduled_date),
        starts_at: String(entry.starts_at || "").slice(0, 5),
        ends_at: String(entry.ends_at || "").slice(0, 5),
      }));
    const generated = [];
    const skipped = [];
    const reservationKeyFor = (unit, component) => `${unit.id}:${component}`;
    const roomPoolFor = (unit, component) =>
      activeRooms.filter(
        (room) =>
          roomCompatibleFor(unit, component, room, teachingRoomIdsFor(unit)) &&
          Number(room?.capacity || 0) >= bookedCandidatesFor(unit),
      );
    const availableRoomFor = (unit, slot) =>
      roomPoolFor(unit, slot.component).find(
        (room) =>
          !reservations.some(
            (occupied) =>
              occupied.roomId &&
              String(occupied.roomId) === String(room.id) &&
              occupied.date === slot.scheduled_date &&
              overlaps(
                occupied.starts_at,
                occupied.ends_at,
                slot.starts_at,
                slot.ends_at,
              ),
          ),
      );
    const reserveSlot = (unit, component, slot) => {
      const trainerId = unit.chief_invigilator?.id || null;
      if (!trainerId) {
        return false;
      }
      const classKey = classKeyFor(unit);
      const combinedGroupKey = combinedGroupKeyFor(unit);
      const sharedRoomId = combinedGroupKey
        ? reservations.find(
            (occupied) =>
              occupied.combinedGroupKey === combinedGroupKey &&
              occupied.component === component &&
              occupied.date === slot.scheduled_date &&
              occupied.starts_at === slot.starts_at &&
              occupied.ends_at === slot.ends_at,
          )?.roomId
        : null;
      const room =
        roomPoolFor(unit, component).find(
          (candidate) =>
            String(candidate.id) ===
            String(sharedRoomId || slot.room_id),
        ) || availableRoomFor(unit, { ...slot, component });
      if (!room) return false;
      if (
        reservations.some(
          (occupied) =>
              occupied.trainerId &&
            String(occupied.trainerId) === String(trainerId) &&
            !(
              combinedGroupKey &&
              occupied.combinedGroupKey === combinedGroupKey &&
              occupied.component === component
            ) &&
            occupied.date === slot.scheduled_date &&
            overlaps(
              occupied.starts_at,
              occupied.ends_at,
              slot.starts_at,
              slot.ends_at,
            ),
        )
      )
        return false;
      if (
        reservations.some((occupied) => {
          if (
            occupied.classKey !== classKey ||
            occupied.date !== slot.scheduled_date
          )
            return false;
          const sameCombinedGroup =
            combinedGroupKey &&
            occupied.combinedGroupKey === combinedGroupKey &&
            occupied.component === component;
          if (sameCombinedGroup) return false;
          if (
            component === "practical" &&
            (occupied.component === "practical" ||
              occupied.component === "theory")
          )
            return true;
          if (
            component === "theory" &&
            occupied.component === "practical"
          )
            return true;
          return (
            component === "theory" &&
            occupied.component === "theory" &&
            theoryTransitionConflict(
              slot.starts_at,
              slot.ends_at,
              occupied.starts_at,
              occupied.ends_at,
            )
          );
        })
      )
        return false;
      if (
        reservations.some(
          (occupied) =>
            occupied.classKey === classKey &&
            !(combinedGroupKey &&
              occupied.combinedGroupKey === combinedGroupKey &&
              occupied.component === component) &&
            occupied.date === slot.scheduled_date &&
            overlaps(
              occupied.starts_at,
              occupied.ends_at,
              slot.starts_at,
              slot.ends_at,
            ),
        )
      )
        return false;
      if (
        reservations.some(
          (occupied) =>
            occupied.roomId &&
            String(occupied.roomId) === String(room.id) &&
            !(combinedGroupKey &&
              occupied.combinedGroupKey === combinedGroupKey &&
              occupied.component === component) &&
            occupied.date === slot.scheduled_date &&
            overlaps(
              occupied.starts_at,
              occupied.ends_at,
              slot.starts_at,
              slot.ends_at,
            ),
        )
      )
        return false;
      const reservedSlot = { ...slot, room_id: room.id };
      reservations.push({
        reservationKey: reservationKeyFor(unit, component),
        component,
        trainerId,
        classKey,
        combinedGroupKey,
        roomId: room.id,
        ...reservedSlot,
      });
      return reservedSlot;
    };
    const reserve = (unit, component, slot) => {
      const reservedSlot = reserveSlot(unit, component, slot);
      if (!reservedSlot) return false;
      generated.push({
        unit,
        component,
        reservationKey: reservationKeyFor(unit, component),
        ...reservedSlot,
      });
      return true;
    };
    const removeReservation = (reservationKey) => {
      const index = reservations.findIndex(
        (reservation) => reservation.reservationKey === reservationKey,
      );
      if (index >= 0) reservations.splice(index, 1);
    };
    const candidateSlotsFor = (unit, component) => {
      const durationMinutes = Math.round(hoursFor(unit, component) * 60);
      const componentDates = dateRange(
        component === "practical"
          ? practicalStartDateFor(settings)
          : settings.assessment_starts_on,
        settings.assessment_ends_on,
      );
      const dayStart = timeMinutes(settings.assessment_starts_at);
      const dayEnd = timeMinutes(settings.assessment_ends_at);
      if (
        dayStart === null ||
        dayEnd === null ||
        dayEnd <= dayStart ||
        durationMinutes <= 0
      )
        return [];
      const candidates = [];
      componentDates.forEach((scheduled_date) => {
        hourlyColumns.forEach((column) => {
          const start = column.startMinutes;
          if (start < dayStart || start + durationMinutes > dayEnd) return;
          const end = start + durationMinutes;
          const starts_at = formatTime(start);
          const ends_at = formatTime(end);
          if (
            settings.break_starts_at &&
            overlaps(
              starts_at,
              ends_at,
              settings.break_starts_at,
              settings.break_ends_at,
            )
          )
            return;
          candidates.push({
            scheduled_date,
            starts_at,
            ends_at,
            notes: `Generated from the hourly ${component} timetable grid`,
          });
        });
      });
      return candidates;
    };

    const scheduleComponentGroups = (component) => {
      const groups = new Map();
      targetUnits
        .filter(
          (unit) =>
            hoursFor(unit, component) > 0 &&
            !existingKeys.has(unit.id + ":" + component),
        )
        .forEach((unit) => {
          const groupKey =
            combinedGroupKeyFor(unit) || `unit:${String(unit.id)}`;
          if (!groups.has(groupKey)) groups.set(groupKey, []);
          groups.get(groupKey).push(unit);
        });

      const orderedGroups = [...groups.values()].sort((left, right) => {
        const duration =
          Math.max(...right.map((unit) => hoursFor(unit, component))) -
          Math.max(...left.map((unit) => hoursFor(unit, component)));
        return (
          duration ||
          String(left[0]?.code || "").localeCompare(
            String(right[0]?.code || ""),
          )
        );
      });

      orderedGroups.forEach((members) => {
        const first = members[0];
        const groupLabel = combinedGroupKeyFor(first)
          ? `combined teaching group ${first.teaching_group_code}`
          : "class";
        if (members.some((unit) => !unit.chief_invigilator?.id)) {
          members.forEach((unit) =>
            skipped.push(
              componentLabel(unit, component) +
                `: ${groupLabel} has no active Unit Trainer`,
            ),
          );
          return;
        }
        if (members.some((unit) => !teachingRoomIdsFor(unit).length)) {
          members.forEach((unit) =>
            skipped.push(
              componentLabel(unit, component) +
                ": no room is declared for this unit in the teaching timetable for this academic period",
            ),
          );
          return;
        }
        if (members.some((unit) => !roomPoolFor(unit, component).length)) {
          members.forEach((unit) => {
            const hasCompatibleRoom = activeRooms.some((room) =>
              roomCompatibleFor(
                unit,
                component,
                room,
                teachingRoomIdsFor(unit),
              ),
            );
            skipped.push(
              componentLabel(unit, component) +
                (hasCompatibleRoom && bookedCandidatesFor(unit) > 0
                  ? `: no teaching timetable room has capacity for ${bookedCandidatesFor(unit)} booked candidate${bookedCandidatesFor(unit) === 1 ? "" : "s"}`
                  : `: no active teaching timetable room is compatible with this ${component} assessment`),
            );
          });
          return;
        }
        const commonTeachingRooms = activeRooms.filter((room) =>
          members.every((unit) =>
            roomCompatibleFor(
              unit,
              component,
              room,
              teachingRoomIdsFor(unit),
            ) && Number(room?.capacity || 0) >= bookedCandidatesFor(unit),
          ),
        );
        if (!commonTeachingRooms.length) {
          members.forEach((unit) =>
            skipped.push(
              componentLabel(unit, component) +
                ": combined teaching units do not share one compatible teaching-timetable room",
            ),
          );
          return;
        }
        const duration = hoursFor(first, component);
        if (
          members.some(
            (unit) =>
              Math.round(hoursFor(unit, component) * 100) !==
              Math.round(duration * 100),
          )
        ) {
          members.forEach((unit) =>
            skipped.push(
              componentLabel(unit, component) +
                ": combined units must declare the same assessment duration to share a timetable slot",
            ),
          );
          return;
        }
        // If one member of a combined teaching group was already scheduled,
        // every newly-added member must join that exact slot. Without this
        // anchor, the free-slot search could place the remaining unit at a
        // different time simply because another room was available.
        const groupKey = combinedGroupKeyFor(first);
        const existingGroupEntries = groupKey
          ? entries.filter((entry) => {
              if (
                entry.status === "cancelled" ||
                entry.assessment_component !== component
              )
                return false;
              const existingUnit =
                entry.unit || unitById[String(entry.unit_id)] || {};
              return combinedGroupKeyFor(existingUnit) === groupKey;
            })
          : [];
        const anchor = existingGroupEntries[0] || null;
        if (
          anchor &&
          existingGroupEntries.some(
            (entry) =>
              toDateValue(entry.scheduled_date) !==
                toDateValue(anchor.scheduled_date) ||
              String(entry.starts_at || "").slice(0, 5) !==
                String(anchor.starts_at || "").slice(0, 5) ||
              String(entry.ends_at || "").slice(0, 5) !==
                String(anchor.ends_at || "").slice(0, 5),
          )
        ) {
          members.forEach((unit) =>
            skipped.push(
              componentLabel(unit, component) +
                ": existing combined teaching-group entries are not on one common slot; resolve them before generating",
            ),
          );
          return;
        }
        const candidates = candidateSlotsFor(first, component).filter(
          (candidate) =>
            !anchor ||
            (candidate.scheduled_date === toDateValue(anchor.scheduled_date) &&
              candidate.starts_at ===
                String(anchor.starts_at || "").slice(0, 5) &&
              candidate.ends_at === String(anchor.ends_at || "").slice(0, 5)),
        );
        if (!candidates.length) {
          members.forEach((unit) =>
            skipped.push(
              componentLabel(unit, component) +
                ": no available hourly slot matches its declared duration",
            ),
          );
          return;
        }

        let placed = false;
        for (const candidate of candidates) {
          // Combined units must use one room that is declared for every
          // member. Trying each common room prevents the first member's room
          // ordering from making an otherwise valid group look unschedulable.
          const candidateRooms = combinedGroupKeyFor(first)
            ? commonTeachingRooms
            : [null];
          for (const candidateRoom of candidateRooms) {
            const reservationCount = reservations.length;
            const reservedMembers = [];
            const candidateWithRoom = candidateRoom
              ? { ...candidate, room_id: candidateRoom.id }
              : candidate;
            for (const unit of members) {
              const reservedSlot = reserveSlot(
                unit,
                component,
                candidateWithRoom,
              );
              if (!reservedSlot) break;
              reservedMembers.push({ unit, slot: reservedSlot });
            }
            if (reservedMembers.length !== members.length) {
              reservations.splice(reservationCount);
              continue;
            }
            reservedMembers.forEach(({ unit, slot }) => {
              generated.push({
                unit,
                component,
                reservationKey: reservationKeyFor(unit, component),
                ...slot,
              });
            });
            placed = true;
            break;
          }
          if (placed) break;
        }
        if (!placed) {
          members.forEach((unit) =>
            skipped.push(
              componentLabel(unit, component) +
                `: no free slot for its ${groupLabel}, Unit Trainer and venue`,
            ),
          );
        }
      });
    };

    scheduleComponentGroups("theory");
    scheduleComponentGroups("practical");

    if (!generated.length) {
      setMessage({
        type: skipped.length ? "error" : "success",
        text: skipped.length
          ? "Nothing was generated. " + skipped.join("; ")
          : "All declared assessment components already have timetable entries.",
      });
      return;
    }

    setSavingKey("bulk");
    setMessage(null);
    const savedEntries = [];
    const saveFailures = [];
    const isSchedulingConflict = (error) =>
      /chief invigilator|trainer.*already.*(exam|time)|another exam|same programme|programme.*level.*module|same class|class group|cohort|transition|same day|one practical|room|venue/i.test(
        String(error?.message || ""),
      );
    const isCapacityConflict = (error) =>
      /capacity|seat|candidates have booked|enrolled students/i.test(
        String(error?.message || ""),
      );
    try {
      // The database remains the final authority for trainer clashes. If a
      // concurrent save or a legacy entry disagrees with the local plan,
      // release the planned reservation and try every remaining valid slot
      // before reporting the component as unscheduled.
      for (const item of generated.map((value) => ({ ...value }))) {
        const reservationKey = item.reservationKey;
        const candidates = [
          item,
          ...candidateSlotsFor(item.unit, item.component).filter(
            (candidate) =>
              candidate.scheduled_date !== item.scheduled_date ||
              candidate.starts_at !== item.starts_at ||
              candidate.ends_at !== item.ends_at,
          ),
        ];
        let saved = null;
        let lastError = null;
        for (const candidate of candidates) {
          if (candidate !== item) {
            removeReservation(reservationKey);
            const reservedSlot = reserveSlot(
              item.unit,
              item.component,
              candidate,
            );
            if (!reservedSlot) continue;
            Object.assign(candidate, reservedSlot);
          }
          try {
            saved = await summativeAssessmentTimetableService.saveEntry({
              academicYearId: selectedPeriod.academicYearId,
              semesterId: selectedPeriod.semesterId,
              unitId: item.unit.id,
              assessmentComponent: item.component,
              scheduledDate: candidate.scheduled_date,
              startsAt: candidate.starts_at,
              endsAt: candidate.ends_at,
              sessionId: null,
              roomId: candidate.room_id || null,
              notes: candidate.notes,
            });
            break;
          } catch (error) {
            lastError = error;
            removeReservation(reservationKey);
            if (isCapacityConflict(error)) break;
            if (!isSchedulingConflict(error)) break;
          }
        }
        if (saved) {
          savedEntries.push(saved);
        } else {
          saveFailures.push(
            componentLabel(item.unit, item.component) +
              ": " +
              (isSchedulingConflict(lastError)
                ? "no available slot after automatic reshuffling"
                : lastError?.message || "could not be saved"),
          );
        }
      }
      setEntries((prev) => {
        const savedKeys = new Set(
          savedEntries.map(
            (entry) => entry.unit_id + ":" + entry.assessment_component,
          ),
        );
        return [
          ...prev.filter(
            (entry) =>
              !savedKeys.has(entry.unit_id + ":" + entry.assessment_component),
          ),
          ...savedEntries,
        ];
      });
      setDrafts((prev) => {
        const next = { ...prev };
        savedEntries.forEach((entry) => {
          next[entry.unit_id + ":" + entry.assessment_component] = {
          scheduled_date: toDateValue(entry.scheduled_date),
          starts_at: String(entry.starts_at || "").slice(0, 5),
          ends_at: String(entry.ends_at || "").slice(0, 5),
          room_id: entry.room_id || entry.room?.id || "",
            notes: entry.notes || "",
          };
        });
        return next;
      });
      const details = [...skipped, ...saveFailures];
      setMessage({
        type: details.length ? "warning" : "success",
        text:
          savedEntries.length +
          " timetable component" +
          (savedEntries.length === 1 ? "" : "s") +
          " generated for " +
          `${activePeriodLabel} · ${scheduleScopeLabel}` +
          (details.length ? ". Attention: " + details.join("; ") : "."),
      });
    } finally {
      setSavingKey("");
    }
  };

  const downloadTimetableLegacy = async () => {
    const timetableElement = document.getElementById(
      "summative-assessment-timetable",
    );
    if (!timetableElement || !entries.length) {
      setMessage({
        type: "error",
        text: "Generate or schedule at least one assessment before downloading the timetable.",
      });
      return;
    }
    setDownloading(true);
    setMessage(null);
    let printableClone = null;
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      // Capture the complete wide table, not only the portion currently
      // visible inside the horizontal scroll container.
      printableClone = timetableElement.cloneNode(true);
      const table = printableClone.querySelector("table");
      const exportWidth = Math.max(
        timetableElement.scrollWidth,
        table?.scrollWidth || 0,
      );
      printableClone.style.position = "fixed";
      printableClone.style.left = "-100000px";
      printableClone.style.top = "0";
      const timetableDates = dateRange(
        settings?.assessment_starts_on,
        settings?.assessment_ends_on,
      );
      const timetableColumns = hourlyTimelineFor(settings);
      // Use a compact print geometry rather than the spacious on-screen
      // geometry. This keeps every configured date on one A4 landscape sheet.
      const compactColumnWidth = 54;
      const compactLabelWidth = 175;
      const fullGridWidth = Math.max(
        compactLabelWidth +
          timetableDates.length * timetableColumns.length * compactColumnWidth,
        Math.min(exportWidth, 1800),
      );
      printableClone.style.width = `${fullGridWidth}px`;
      printableClone.style.maxWidth = "none";
      printableClone.style.background = "#ffffff";
      const clonedTable = printableClone.querySelector("table");
      if (clonedTable) {
        clonedTable.style.width = `${fullGridWidth}px`;
        clonedTable.style.minWidth = `${fullGridWidth}px`;
        clonedTable.querySelectorAll("th, td").forEach((cell) => {
          cell.style.minWidth = "0";
          cell.style.padding = "2px 3px";
          cell.style.fontSize = "8px";
        });
        clonedTable.querySelectorAll("thead tr:nth-child(2) th").forEach(
          (cell) => {
            cell.style.width = `${compactColumnWidth}px`;
            cell.style.minWidth = `${compactColumnWidth}px`;
          },
        );
        clonedTable.querySelectorAll("thead th:first-child, tbody th").forEach(
          (cell) => {
            cell.style.width = `${compactLabelWidth}px`;
            cell.style.minWidth = `${compactLabelWidth}px`;
          },
        );
      }
      const headerTitle = printableClone.querySelector("h2");
      if (headerTitle) headerTitle.textContent = "Summative examination timetable";
      const headerDescription = headerTitle?.parentElement?.querySelector("p");
      if (headerDescription) headerDescription.style.display = "none";
      const headerBadge = headerTitle?.closest("div.flex")?.querySelector("span");
      if (headerBadge) headerBadge.style.display = "none";
      printableClone
        .querySelectorAll(".overflow-x-auto")
        .forEach((wrapper) => {
          wrapper.style.overflow = "visible";
          wrapper.style.width = `${fullGridWidth}px`;
          wrapper.style.maxWidth = "none";
        });
      document.body.appendChild(printableClone);
      const scale = Math.max(1, Math.min(2, 3000 / fullGridWidth));
      const canvas = await html2canvas(printableClone, {
        backgroundColor: "#ffffff",
        scale,
        useCORS: true,
        width: printableClone.scrollWidth,
        height: printableClone.scrollHeight,
        windowWidth: printableClone.scrollWidth,
        windowHeight: printableClone.scrollHeight,
        logging: false,
      });
      const image = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 5;
      const contentWidth = pageWidth - margin * 2;
      const contentHeight = pageHeight - margin * 2;
      const fitScale = Math.min(
        contentWidth / Math.max(canvas.width, 1),
        contentHeight / Math.max(canvas.height, 1),
      );
      const imageWidth = canvas.width * fitScale;
      const imageHeight = canvas.height * fitScale;
      pdf.addImage(
        image,
        "PNG",
        (pageWidth - imageWidth) / 2,
        margin,
        imageWidth,
        imageHeight,
      );
      const filename = `summative-assessment-timetable-${activePeriodLabel
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase()}.pdf`;
      pdf.setProperties({
        title: `Summative assessment timetable · ${activePeriodLabel}`,
        subject: activePeriodLabel,
        creator: "College Management System",
      });
      pdf.save(filename);
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "The timetable could not be downloaded.",
      });
    } finally {
      printableClone?.remove();
      setDownloading(false);
    }
  };

  const downloadTimetable = async () => {
    if (!visibleEntries.length) {
      setMessage({
        type: "error",
        text: `Generate or schedule at least one assessment for ${scheduleScopeLabel} before downloading the timetable.`,
      });
      return;
    }
    setDownloading(true);
    setMessage(null);
    try {
      const { downloadSummativeAssessmentTimetablePdf } = await import(
        "./utils/summativeAssessmentTimetablePdf"
      );
      await downloadSummativeAssessmentTimetablePdf({
        settings,
        entries: visibleEntries,
        units: visibleUnits,
        periodLabel: `${activePeriodLabel} · ${scheduleScopeLabel}`,
        reportView,
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "The timetable could not be downloaded.",
      });
    } finally {
      setDownloading(false);
    }
  };

  const activePeriodLabel = selected
    ? periodName(selected.year, selected.semester)
    : "No academic period selected";

  const content = (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-bold text-slate-950">
                Summative assessment timetable
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={generateDraftTimetable}
              disabled={
                periodLoading || savingKey === "bulk" || downloading || !timetableReady
              }
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Clock3 className="h-3.5 w-3.5" />
              {savingKey === "bulk"
                ? "Generating…"
                : "Generate / reshuffle draft timetable"}
            </button>
            <button
              type="button"
              onClick={downloadTimetable}
              disabled={
                periodLoading || savingKey === "bulk" || downloading || !visibleEntries.length
              }
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              title="Download the generated timetable as a PDF"
            >
              <Download className="h-3.5 w-3.5" />
              {downloading ? "Preparing PDF…" : "Download PDF"}
            </button>
            <select
              value={reportView}
              onChange={(event) => setReportView(event.target.value)}
              aria-label="Exam timetable report"
              title="ASC-style timetable report"
              className="h-9 max-w-[205px] rounded-lg border border-blue-200 bg-blue-50 px-2 text-xs font-semibold text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SUMMATIVE_REPORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={loadPeriodData}
              disabled={periodLoading || savingKey === "bulk"}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${periodLoading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-[minmax(0,1.25fr)_minmax(220px,0.9fr)_minmax(220px,0.9fr)_auto] md:items-end">
          <label className="space-y-1.5">
            <span className="text-xs font-semibold text-slate-700">
              Academic period
            </span>
            <select
              value={`${selectedPeriod.academicYearId}:${selectedPeriod.semesterId}`}
              onChange={(e) => {
                const [academicYearId, semesterId] = e.target.value.split(":");
                setSelectedPeriod({ academicYearId, semesterId });
              }}
              disabled={loading || !periods.length}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="">Select academic year and semester…</option>
              {periods.map((period) => (
                <option
                  key={`${period.year.id}:${period.semester.id}`}
                  value={`${period.year.id}:${period.semester.id}`}
                >
                  {periodName(period.year, period.semester)}
                  {String(period.semester.status).toLowerCase() === "active"
                    ? " · Active"
                    : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-semibold text-slate-700">Programme</span>
            <select
              value={selectedProgramId}
              onChange={(event) => {
                setSelectedProgramId(event.target.value);
                setSelectedUnitId("all");
              }}
              disabled={periodLoading || !programmeOptions.length}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="all">All programmes</option>
              {programmeOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.name}{option.code ? ` (${option.code})` : ""}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-semibold text-slate-700">Unit of competency</span>
            <select
              value={selectedUnitId}
              onChange={(event) => setSelectedUnitId(event.target.value)}
              disabled={periodLoading || !unitOptions.length}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <option value="all">All units</option>
              {unitOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.code ? `${option.code} · ` : ""}{option.name}</option>
              ))}
            </select>
          </label>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-600">
            <b className="text-slate-900">{entries.length}</b> scheduled
            component{entries.length === 1 ? "" : "s"} · {scheduleScopeLabel} · {activePeriodLabel}
          </div>
        </div>
      </section>

      {message && (
        <div
          role="status"
          className={`flex items-start gap-2 rounded-xl border p-3 text-xs font-medium ${message.type === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`}
        >
          {message.type === "success" ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {timetableReady && teachingTimetableIssue && !periodLoading && (
        <section className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-950">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <div>
            <p className="text-sm font-bold">
              Teaching timetable rooms could not be loaded
            </p>
            <p className="mt-0.5 text-xs text-amber-800">
              {teachingTimetableIssue}
            </p>
          </div>
        </section>
      )}

      {!timetableReady && !periodLoading && selectedPeriod.academicYearId && (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-950">
          <div className="flex items-start gap-2">
            <Settings2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
            <div>
              <p className="text-sm font-bold">
                Assessment timetable is not ready for this period
              </p>
              <p className="mt-0.5 text-xs text-amber-800">
                {settingsIssue ||
                  (embedded
                    ? "Configure timetable settings to continue."
                    : "Configure this period in System Settings → Timetable.")}
              </p>
            </div>
          </div>
          {!embedded && (
            <button
              type="button"
              onClick={() =>
                navigate("/system-settings", {
                  state: { section: "timetable" },
                })
              }
              className="rounded-lg bg-amber-700 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-800"
            >
              Open timetable settings
            </button>
          )}
        </section>
      )}

      {timetableReady && (
        <section className="grid gap-3 rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 text-xs text-indigo-950 md:grid-cols-5">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            <span>
              <b>Dates:</b> {settings.assessment_starts_on || "—"} →{" "}
              {settings.assessment_ends_on || "—"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            <span>
              <b>Practicals from:</b> {practicalStartDateFor(settings) || "—"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Clock3 className="h-4 w-4" />
            <span>
              <b>Day:</b>{" "}
              {String(settings.assessment_starts_at || "").slice(0, 5)} →{" "}
              {String(settings.assessment_ends_at || "").slice(0, 5)}
            </span>
          </div>
          <div>
            <b>Break:</b>{" "}
            {settings.break_starts_at
              ? `${String(settings.break_starts_at).slice(0, 5)} → ${String(settings.break_ends_at).slice(0, 5)}`
              : "None"}
          </div>
        </section>
      )}

      {timetableReady && visibleEntries.length > 0 && (
        <GeneratedTimetableGrid
          settings={settings}
          entries={visibleEntries}
          units={visibleUnits}
          periodLabel={`${activePeriodLabel} · ${scheduleScopeLabel}`}
          reportView={reportView}
        />
      )}

      {timetableReady ? (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div>
              <h2 className="text-base font-bold text-slate-950">
                Units requiring summative assessment
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">Generate slots independently per programme and unit of competency.</p>
            </div>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
              {visibleUnits.length} unit{visibleUnits.length === 1 ? "" : "s"}
            </span>
          </div>
          {periodLoading ? (
            <div className="p-12 text-center text-sm text-slate-500">
              Loading period timetable…
            </div>
          ) : !visibleUnits.length ? (
            <div className="p-12 text-center text-sm text-slate-500">
              No units with declared theory or practical assessment hours match
              the selected programme and unit of competency.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1050px] border-collapse text-left">
                <thead className="bg-slate-50 text-xs text-slate-600">
                  <tr>
                    <th className="w-64 border-b border-slate-200 px-5 py-3 font-bold">
                      Unit
                    </th>
                    <th className="w-36 border-b border-slate-200 px-3 py-3 font-bold">
                      Structure
                    </th>
                    <th className="border-b border-slate-200 px-3 py-3 font-bold">
                      Theory assessment
                    </th>
                    <th className="border-b border-slate-200 px-3 py-3 font-bold">
                      Practical assessment
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleUnits.map((unit) => (
                    <tr
                      key={unit.id}
                      className="align-top hover:bg-slate-50/50"
                    >
                      <td className="px-5 py-4">
                        <div className="font-mono text-xs font-bold text-primary">
                          {unit.code}
                        </div>
                        <div className="mt-1 text-[0.65rem] font-medium text-slate-500">
                          {programmeLabel(unit)}
                        </div>
                        <div className="mt-1 text-sm font-semibold text-slate-900">
                          {unit.name}
                        </div>
                        <div
                          className={`mt-2 text-[0.65rem] font-medium ${unit.chief_invigilator?.full_name ? "text-slate-500" : "text-amber-700"}`}
                        >
                          Unit Trainer / chief invigilator:{" "}
                          {unit.chief_invigilator?.full_name || "Not assigned"}
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <StructureLabel unit={unit} />
                      </td>
                      <td className="px-3 py-4">
                        <TimetableCell
                          unit={unit}
                          component="theory"
                          value={draftFor(unit, "theory")}
                          onChange={(field, value) =>
                            updateDraft(unit, "theory", field, value)
                          }
                          onSave={() => saveDraft(unit, "theory")}
                          saving={savingKey === `${unit.id}:theory`}
                          disabled={periodLoading || !timetableReady}
                          settings={settings}
                          rooms={rooms}
                          teachingRoomIds={teachingRoomIdsFor(unit)}
                        />
                      </td>
                      <td className="px-3 py-4">
                        <TimetableCell
                          unit={unit}
                          component="practical"
                          value={draftFor(unit, "practical")}
                          onChange={(field, value) =>
                            updateDraft(unit, "practical", field, value)
                          }
                          onSave={() => saveDraft(unit, "practical")}
                          saving={savingKey === `${unit.id}:practical`}
                          disabled={periodLoading || !timetableReady}
                          settings={settings}
                          rooms={rooms}
                          teachingRoomIds={teachingRoomIdsFor(unit)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : !periodLoading && selectedPeriod.academicYearId ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <Settings2 className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
            <div>
              <h2 className="text-base font-bold text-slate-950">
                Unit scheduling is locked
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Configure this period's timetable settings to continue.
              </p>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );

  return embedded ? (
    content
  ) : (
    <AdminLayout title="Summative Assessment Timetable">{content}</AdminLayout>
  );
};

export default SummativeAssessmentScheduleManagement;
